export const getOrderChargesArray = (order) => {
  if (!order) return [];
  if (Array.isArray(order.saleCharges) && order.saleCharges.length > 0) {
    return order.saleCharges;
  }
  if (Array.isArray(order.sale_charges) && order.sale_charges.length > 0) {
    return order.sale_charges;
  }
  if (Array.isArray(order.orderCharges) && order.orderCharges.length > 0) {
    return order.orderCharges;
  }
  if (Array.isArray(order.order_charges) && order.order_charges.length > 0) {
    return order.order_charges;
  }
  return [];
};

export const resolveChargeKind = (charge) => {
  if (!charge) return 'external';
  if (charge.kind) return String(charge.kind);
  const label = String(charge.label || '').toLowerCase();
  if (label.includes('interna')) return 'internal';
  if (label.includes('externa')) return 'external';
  return 'external';
};

const roundCurrency = (value) => Math.round(value * 100) / 100;

export const computeOrderChargeBreakdown = (order, options = {}) => {
  const baseAmount = options.baseAmount != null
    ? Number(options.baseAmount)
    : Number(order?.amount ?? order?.base_amount ?? 0);
  const base = Number.isFinite(baseAmount) ? baseAmount : 0;
  const charges = getOrderChargesArray(order);
  const txType = String(order?.transaction_type ?? order?.type ?? '').toLowerCase();

  const sums = charges.reduce(
    (acc, charge) => {
      const raw = Number(charge?.value ?? charge?.amount ?? 0);
      if (!raw) return acc;
      const type = String(charge?.type || 'flat');
      const computed = type === 'percent' ? roundCurrency((base * raw) / 100) : raw;
      if (!computed) return acc;
      const kind = resolveChargeKind(charge);
      if (kind === 'internal') {
        acc.internal += computed;
      } else {
        acc.external += computed;
      }
      return acc;
    },
    { external: 0, internal: 0 }
  );

  const totalCost = roundCurrency(base + sums.external);
  // Cash flow logic:
  // - Orders: Branch cash impact is base minus internal commission; external fees not in branch cash
  // - Transfers: External fees paid via bank (not cash), internal reduces cash sent
  // - Sales: Not used here for cash impact
  const cashNeeded = txType === 'transfer'
    ? roundCurrency(base - sums.internal)
    : txType === 'order'
    ? roundCurrency(base - sums.internal)
    : roundCurrency(totalCost - sums.internal);

  return {
    base,
    external: roundCurrency(sums.external),
    internal: roundCurrency(sums.internal),
    totalCost,
    cashNeeded,
  };
};

export const computeOrderCashNeeded = (order, options = {}) => {
  const breakdown = computeOrderChargeBreakdown(order, options);
  return breakdown.cashNeeded;
};

export const computeSaleChargeBreakdown = (sale, options = {}) => {
  const baseAmount = options.baseAmount != null
    ? Number(options.baseAmount)
    : Number(sale?.amount ?? sale?.base_amount ?? 0);
  const base = Number.isFinite(baseAmount) ? baseAmount : 0;
  const breakdown = computeOrderChargeBreakdown(sale, { ...options, baseAmount: base });

  let internal = Number(breakdown.internal || 0) || 0;
  let external = Number(breakdown.external || 0) || 0;

  if (internal === 0 && external === 0) {
    const legacyPct = Number(sale?.salePercentage ?? sale?.sale_percentage ?? 0) || 0;
    if (legacyPct !== 0 && base > 0) {
      internal = roundCurrency((base * legacyPct) / 100);
    }
  }

  // For sales:
  // - totalToCollect: what the client pays us (base + external)
  // - totalToDeliver: what we deliver to the client (base - internal)
  const totalToCollect = roundCurrency(base + external);
  const totalToDeliver = roundCurrency(Math.max(0, base - internal));

  return {
    base,
    internal,
    external,
    totalToCollect,
    totalToDeliver,
  };
};
